import { type OrderDraft } from 'components/Account/types/OrderDraft.types'
import {
  type AiGeneratedCover,
  type Cover,
} from 'components/PhotoBook/PhotoBookProvider/PhotoBook.types'
import { isAiGeneratedCover } from 'components/PhotoBook/PhotoBookProvider/PhotoBook.utils'
import { getPhotoBankStore, getPhotoBooksStore } from 'stores/RootStoreProvider'
import {
  readMobxSnapshot,
  readOutsideReaction,
} from 'utils/mobx/readOutsideReaction'
import { logger } from '../logger'
import { deserializeAccountPhotoBook } from './photoBookDTO'
import { type SerializedPhotoBook } from './photoBookDTO.types'
import { LEGACY_PHOTO_BOOK_ID, mintPhotoBookId } from './photoBookId'

/**
 * The one reader of an order draft's books: the books map, plus the singular
 * field as one more entry with its sibling quantity folded in while nothing in
 * the map or the tombstones accounts for the book it holds. The singular shape
 * is read indefinitely, since per-order snapshots are durable records.
 */
export function getOrderDraftSerializedPhotoBooks(
  orderDraft: OrderDraft
): SerializedPhotoBook[] {
  const deletedBookIds = orderDraft.deletedBookIds ?? []
  const photoBooks = orderDraft.photoBooks ?? {}

  // A tombstoned id the server still returns in the map would be deleted and
  // received straight back, since the reader deletes before it receives.
  //
  // The key carries the identity, not the entry: it is what the server merges
  // and tombstones by, and an entry whose own bookId the store's schema drops
  // would otherwise read back as the one legacy book and collapse every book
  // of the map into it.
  const mappedBooks = Object.entries(photoBooks)
    .filter(([bookId]) => !deletedBookIds.includes(bookId))
    .map(([bookId, photoBook]) => ({ ...photoBook, bookId }))

  const { photoBook } = orderDraft
  if (!photoBook) {
    return mappedBooks
  }

  // The server never clears the singular slot. A map-era client carries the
  // book in it under the id below, into the map when it is edited and into the
  // tombstones when it is deleted. Until one of those lands the slot is that
  // book's only home, and reading it after either would resurrect a book the
  // user moved on from.
  const legacyBookId = photoBook.bookId ?? LEGACY_PHOTO_BOOK_ID
  if (
    Object.keys(photoBooks).includes(legacyBookId) ||
    deletedBookIds.includes(legacyBookId)
  ) {
    return mappedBooks
  }

  return [
    {
      ...photoBook,
      bookId: legacyBookId,
      quantity: orderDraft.quantity ?? photoBook.quantity,
    },
    ...mappedBooks,
  ]
}

/**
 * A past order's books as the order printed them. Checkout writes the printed
 * book to the singular slot, while the map is whatever the live draft held at
 * checkout: a copy that fell behind the printed one, or every book of a group
 * checkout. So the singular book always wins, and its same-id map entry only
 * lends the AI cover gallery and quantity, which checkout doesn't write.
 */
export function getOrderSnapshotSerializedPhotoBooks(
  orderDraft: OrderDraft
): SerializedPhotoBook[] {
  const { photoBook } = orderDraft
  if (!photoBook) {
    return getOrderDraftSerializedPhotoBooks(orderDraft)
  }

  const sameIdBook: SerializedPhotoBook | undefined =
    orderDraft.photoBooks?.[photoBook.bookId ?? LEGACY_PHOTO_BOOK_ID]
  if (!sameIdBook) {
    return [
      { ...photoBook, quantity: orderDraft.quantity ?? photoBook.quantity },
    ]
  }

  return [
    {
      ...photoBook,
      quantity: sameIdBook.quantity,
      aiGeneratedCovers: sameIdBook.aiGeneratedCovers,
    },
  ]
}

// The Designs grid marks the active cover by finding it in the gallery, and a
// cover missing from it can't be picked again once the user switches away.
function getAiGeneratedCoversWithActiveCover(
  cover: Cover,
  aiGeneratedCovers: AiGeneratedCover[]
): AiGeneratedCover[] {
  if (
    !isAiGeneratedCover(cover) ||
    aiGeneratedCovers.some(
      (aiGeneratedCover) => aiGeneratedCover.coverUrl === cover.coverUrl
    )
  ) {
    return aiGeneratedCovers
  }

  const { bookCaption, ...generatedCover } = cover
  return [generatedCover, ...aiGeneratedCovers]
}

/**
 * A past order's books on their way back to the editor. Ordering again means
 * another copy, so every book gets a fresh id — reusing the ordered one would
 * make the merge swallow the reorder into the book it was ordered from.
 *
 * The caller has just put the order's photos in the bank, which is what the
 * books' photos resolve against, so a book that fails to come back together
 * here is a failure rather than a wait.
 *
 * Returns the ids the copies landed under, in the order the draft lists them,
 * so the caller can open one: a bare editor URL would resolve to whichever
 * book this browser was already showing.
 */
export async function receiveReorderedPhotoBooks(
  orderDraft: OrderDraft
): Promise<string[]> {
  const accountPhotos = readMobxSnapshot(() => getPhotoBankStore().remotePhotos)
  const reorderedBookIds: string[] = []
  for (const serializedBook of getOrderSnapshotSerializedPhotoBooks(
    orderDraft
  )) {
    const photoBook = deserializeAccountPhotoBook({
      photoBook: serializedBook,
      accountPhotos,
      quantity: serializedBook.quantity,
    })
    if (!photoBook) {
      logger.error(
        'Order again: could not read the photo book from the order',
        { hasOrderPhotoBook: true }
      )
      continue
    }

    logger.info('Order again: receiving photo book from order draft', {
      'Pages Count': photoBook.pages.length,
    })
    const bookId = mintPhotoBookId()
    // The ordered book's own sort key would drop its copy back among the books
    // the user already holds; a reorder is a new book and belongs after them.
    const receivedAt = Date.now()
    await getPhotoBooksStore().receiveExternalBook(
      {
        ...photoBook,
        bookId,
        lastEditedAt: receivedAt,
        sortKey: receivedAt,
        aiGeneratedCovers: getAiGeneratedCoversWithActiveCover(
          photoBook.cover,
          photoBook.aiGeneratedCovers
        ),
      },
      'Reorder'
    )

    // A copy whose write did not land, or that a sign-out wiped mid-flight, is
    // rolled back out of the map, and naming it would open an id with no book.
    if (readOutsideReaction(() => getPhotoBooksStore().booksMap.has(bookId))) {
      reorderedBookIds.push(bookId)
    }
  }

  return reorderedBookIds
}
